import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  skipToken,
  type TypedUseQueryStateResult,
} from '@reduxjs/toolkit/query/react';
import {
  type ExperienceCard,
  type ExperienceCompany,
  type ExperienceConfirmCancelCard,
  type ExperienceMultiRowSurveyCard,
  type ExperienceSimplePleaCard,
  type ExperienceSimplePleaWithReturnCard,
  type PricingPageHeaderCard,
} from '@bbk/retention-sdk/contracts/experience-cards';
import { ExperienceCardTemplate } from '@bbk/retention-sdk/contracts/layout';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import {
  bbkApiSlice,
  type BBK_FETCH_BASE_QUERY,
} from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { DEFAULT_BRAND_ID } from '@bbkAdminRedux/rtkq/brands.slice';
import {
  type AppKey,
  type CompanyKey,
  type DateString,
} from '@bbkAdminRedux/rtkq/current-user.slice';
import { type RestAbstractCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  type RestCard,
  type RestExperience,
} from '@bbkAdminUtils/api-client/rest-experiences-api';
import { type PricingTable } from '@bbkPricingPage/types/pricing-table-types';
import { type EligibleLALayouts } from './placements.utils';

export {
  type ExperienceSimpleHeaderCard,
  type ExperienceHostedFooterCard,
  type ExperienceFullWidthHeaderCard,
  type ExperienceSimplePleaCard,
  type ExperienceSimplePleaWithReturnCard,
  type ExperienceMultiSurveyCardCompetitionSection,
  type ExperienceMultiSurveyCardSentimentSection,
  type ExperienceMultiSurveyCardFeedbackSection,
  type ExperienceMultiSurveyCardReasonSection,
  type ExperienceMultiSurveyCardConfirmationSection,
  type ExperienceMultiSurveyCardSection,
  type ExperienceMultiRowSurveyCard,
  type ExperienceConfirmCancelCard,
  type ExperienceCard,
  type PricingPageHeaderCard,
} from '@bbk/retention-sdk/contracts/experience-cards';

export {
  ExperienceCardTemplate,
  DestinationType,
  ExperienceMultiSurveyCardSectionName,
} from '@bbk/retention-sdk/contracts/layout';

type ExperienceValidationError = {
  detail: string;
  message: string;
  path: string;
  reference_error: boolean;
  type: string;
};

type ExperienceIntegrations = {
  email?: {
    default: string; // email
  };
};
export type ExperienceStyleOverlay = RestBranding['style_overlay'];

export const isExperienceSimplePleaCard = (
  card: RestCard
): card is ExperienceSimplePleaCard =>
  card.template === ExperienceCardTemplate.simple_plea;

export const isMultiRowSurveyCard = (
  card: RestCard
): card is ExperienceMultiRowSurveyCard =>
  card.template === ExperienceCardTemplate.multi_row_survey;

export type PricingPageCard = PricingPageHeaderCard;

export type PricingTableCard = RestAbstractCard & {
  template: ExperienceCardTemplate.pricing_table;
  pricing_table: PricingTable;
};

export type ExperiencePricingPageHeaderCard = RestAbstractCard & {
  template: ExperienceCardTemplate.pricing_page_header;
  requires: {
    header: {
      title: string;
      subtitle: string;
    };
  };
};

export type PricingPageLayout = {
  id: string; // uuid like
  style_overlay: ExperienceStyleOverlay;
  cards: PricingPageCard[];
  pricingCard?: PricingTableCard;
};

type ExperienceLayout = {
  id: string; // uuid like
  style_overlay: ExperienceStyleOverlay;
  cards: ExperienceCard[];
  pricing_table?: PricingTable;
};

export type AssembledExperience = {
  id: string; // uuid "135-7f3de81c-a738-4bd8-b74e-c90e61cd41fa"
  session: string; // string for evaluation in the runtime "${value('session_id')}"
  version: string; // sha-like "20c1b9d6"
  company: ExperienceCompany;
  integrations: ExperienceIntegrations;
  layout: ExperienceLayout;
  lossAversionLayout?: (typeof EligibleLALayouts)[number]['label'];
};

export type ExperienceResponse = {
  draft_publish_date: DateString;
  draft_published: boolean;
  draft_version: `${number}`;
  validation_errors: ExperienceValidationError[];
  experience: AssembledExperience;
};

type GetExperienceArgs = {
  companyKey: CompanyKey;
  appKey: AppKey;
  funnelId: number | string;
  last_draft?: boolean;
  // Non-primary locale to resolve translated content for. Omit for the primary
  // (base) language.
  locale?: string;
};
type UpdatePropArgs = {
  appKey: AppKey;
  funnelId: number | string;
  element: string;
  expectedLastDraftVersion: string;
  path: string;
};
type UpdateElementArgs = {
  appKey: AppKey;
  funnelId: number | string;
  element: ExperienceCard;
  expectedLastDraftVersion: string;
  // When set, the card's translatable strings are saved for this locale only.
  locale?: string;
};

export type LocaleTranslationState = 'ENABLED' | 'DISABLED' | 'UNTRANSLATED';

export type ResourceLocaleState = {
  locale: string;
  state: LocaleTranslationState;
};

type CancelPageLocaleStatesArgs = {
  appKey: string;
  funnelId: number | string;
  /** draft | live locale states. */
  publishState?: 'draft' | 'live';
};
type SetCancelPageLocaleStateArgs = CancelPageLocaleStatesArgs & {
  locale: string;
  enabled: boolean;
};
type UpdateBrandingArgs = {
  appKey: string;
  brandId?: string;
  company: {
    logo?: string;
    name?: string;
    brandingLogoActivated?: boolean;
  };
  style_overlay: unknown;
};
type GetBrandingArgs = {
  brandId?: string;
  appKey: string;
};

export type RestBranding = {
  company: RestExperience['company'];
  style_overlay: RestExperience['layout']['style_overlay'];
};
type PublishExperienceArgs = {
  appKey: string;
  funnelId: string | number;
  expectedLastDraftVersion: string;
};
export const experienceSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getExperience: build.query<ExperienceResponse, GetExperienceArgs>({
      query: ({ companyKey, appKey, funnelId, last_draft, locale }) => ({
        url: `/api/company/${companyKey}/app/${appKey}/funnel/${funnelId}`,
        params: {
          last_draft,
          ...(locale ? { locale } : {}),
        },
      }),
      providesTags: (result, error, arg, meta) => [
        {
          type: 'Experience',
          id: `${arg.funnelId}-${arg.last_draft ? 'draft' : 'live'}-${
            arg.locale ?? 'base'
          }`,
        },
        // Shared across all locales for this funnel+draft/live so a base
        // structural write can refresh every cached locale view.
        {
          type: 'Experience',
          id: `${arg.funnelId}-${arg.last_draft ? 'draft' : 'live'}`,
        },
        {
          type: 'Experience',
          id: 'ALL',
        },
      ],
    }),
    publishExperience: build.mutation<void, PublishExperienceArgs>({
      query: ({ appKey, funnelId, ...body }) => ({
        url: `/api/v1/apps/${appKey}/funnels/${funnelId}/experiences/publishings`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg, meta) => [
        { type: 'Funnels' },
        // Publish spans every locale, so refresh all cached experience reads.
        { type: 'Experience', id: 'ALL' },
      ],
    }),
    getBranding: build.query<RestBranding, GetBrandingArgs>({
      query: ({ appKey, brandId = DEFAULT_BRAND_ID }) =>
        `/api/v1/apps/${appKey}/brands/${brandId}/branding`,
      providesTags: (result, error, arg, meta) => [
        {
          type: 'Branding',
          id: arg.brandId ?? DEFAULT_BRAND_ID,
        },
      ],
    }),
    updateBranding: build.mutation<void, UpdateBrandingArgs>({
      query: ({ appKey, brandId = DEFAULT_BRAND_ID, ...body }) => ({
        url: `/api/v1/apps/${appKey}/brands/${brandId}/branding`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, arg, meta) => [
        {
          type: 'Branding',
          id: arg.brandId ?? DEFAULT_BRAND_ID,
        },
        {
          type: 'AdditionalBrands',
          id: arg.brandId ?? DEFAULT_BRAND_ID,
        },
        {
          type: 'Experience',
        },
      ],
    }),
    updateExperienceElement: build.mutation<
      { draft_version: string },
      UpdateElementArgs
    >({
      query: ({
        appKey,
        funnelId,
        element,
        expectedLastDraftVersion,
        locale,
      }) => ({
        url: `/api/v1/apps/${appKey}/funnels/${funnelId}/experiences`,
        method: 'POST',
        params: locale ? { locale } : undefined,
        body: {
          action: 'update',
          path: `/layout/cards/${element.id}`,
          element,
          expectedLastDraftVersion,
        },
      }),
      invalidatesTags: (result, error, arg, meta) => [
        { type: 'Funnels' },
        // Translation saves only touch that locale's strings. Base (structural)
        // saves can change shared card shape, so refresh every draft locale.
        arg.locale
          ? {
              type: 'Experience' as const,
              id: `${arg.funnelId}-draft-${arg.locale}`,
            }
          : { type: 'Experience' as const, id: `${arg.funnelId}-draft` },
        // A first translation save flips a locale to toggleable, so refresh the
        // per-funnel locale states alongside the experience cache.
        ...(arg.locale
          ? [{ type: 'CancelPageLocaleStates' as const, id: `${arg.funnelId}` }]
          : []),
      ],
    }),
    updateExperienceProp: build.mutation<
      { draft_version: string },
      UpdatePropArgs
    >({
      query: ({
        appKey,
        funnelId,
        element,
        expectedLastDraftVersion,
        path,
      }) => ({
        url: `/api/v1/apps/${appKey}/funnels/${funnelId}/experiences`,
        method: 'POST',
        body: {
          action: 'update',
          path,
          element,
          expectedLastDraftVersion,
        },
      }),
      invalidatesTags: (result, error, arg, meta) => [
        { type: 'Funnels' },
        // Structural props are primary-only but shared across locales.
        { type: 'Experience', id: `${arg.funnelId}-draft` },
      ],
    }),
    getCancelPageLocaleStates: build.query<
      ResourceLocaleState[],
      CancelPageLocaleStatesArgs
    >({
      query: ({ appKey, funnelId, publishState }) => ({
        url: `/api/v1/apps/${appKey}/translations/cancel_page/${funnelId}/locales`,
        ...(publishState ? { params: { publish_state: publishState } } : {}),
      }),
      providesTags: (result, error, { funnelId }) => [
        { type: 'CancelPageLocaleStates', id: `${funnelId}` },
      ],
    }),
    setCancelPageLocaleState: build.mutation<
      void,
      SetCancelPageLocaleStateArgs
    >({
      query: ({ appKey, funnelId, locale, enabled }) => ({
        url: `/api/v1/apps/${appKey}/translations/cancel_page/${funnelId}/locales/${locale}/status`,
        method: 'PUT',
        body: { enabled },
      }),
      invalidatesTags: (result, error, { funnelId }) => [
        { type: 'CancelPageLocaleStates', id: `${funnelId}` },
      ],
    }),
  }),
});

type DesignerExperienceArgs = Pick<GetExperienceArgs, 'funnelId'> & {
  /** Resolved editing locale; undefined requests the primary language. */
  locale: string | undefined;
};

export const useSelectFunnelExperienceFull = (args: DesignerExperienceArgs) => {
  const companyKey = useSelector(selectCompanyInternalName);
  const appKey = useSelector(selectAppId);
  const { funnelId, locale } = args;

  return experienceSlice.endpoints.getExperience.useQuery(
    companyKey && appKey
      ? {
          companyKey,
          appKey,
          funnelId,
          last_draft: true,
          ...(locale ? { locale } : {}),
        }
      : skipToken
  );
};
export const useSelectFunnelExperience = (args: DesignerExperienceArgs) => {
  const { data } = useSelectFunnelExperienceFull(args);

  return data;
};

export const useSelectFunnelExperiencePlea = (args: DesignerExperienceArgs) => {
  const experience = useSelectFunnelExperience(args);

  const isPleaCard = (c: ExperienceCard): c is ExperienceSimplePleaCard =>
    c.template === ExperienceCardTemplate.simple_plea;

  return useMemo(
    () => experience?.experience.layout.cards.find(isPleaCard),
    [experience]
  );
};

export const useSelectFunnelExperiencePleaWithReturn = (
  args: DesignerExperienceArgs
) => {
  const experience = useSelectFunnelExperience(args);

  const isPleaWithReturnCard = (
    c: ExperienceCard
  ): c is ExperienceSimplePleaWithReturnCard =>
    c.template === ExperienceCardTemplate.simple_plea_with_return;

  return useMemo(
    () => experience?.experience.layout.cards.find(isPleaWithReturnCard),
    [experience]
  );
};

export const useSelectFunnelExperienceMultiRowSurveyCard = (
  args: DesignerExperienceArgs
) => {
  const experience = useSelectFunnelExperience(args);

  return useMemo(
    () => experience?.experience.layout.cards.find(isMultiRowSurveyCard),
    [experience]
  );
};

export const useSelectFunnelExperienceConfirmCancelCard = (
  args: DesignerExperienceArgs
) => {
  const experience = useSelectFunnelExperience(args);

  const isConfirmCancelCard = (
    c: ExperienceCard
  ): c is ExperienceConfirmCancelCard =>
    c.template === ExperienceCardTemplate.modal_confirm_cancel;

  return useMemo(
    () => experience?.experience.layout.cards.find(isConfirmCancelCard),
    [experience]
  );
};

type ExperienceSelectorArgs = TypedUseQueryStateResult<
  ExperienceResponse,
  GetExperienceArgs,
  typeof BBK_FETCH_BASE_QUERY
>;
// An example of selectFromResult function
export const selectExperience = (res: ExperienceSelectorArgs) => res;
